import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import Card from '../../../components/ui/Card'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import Spinner from '../../../components/ui/Spinner'
import Modal from '../../../components/ui/Modal'
import Input from '../../../components/ui/Input'
import api from '../../../lib/api'
import { formatCurrency, formatDate } from '../../../lib/formatters'
import type { Invoice, InvoiceStatus } from '../../../types'
import toast from 'react-hot-toast'

// Lab Profile response interface (matches API snake_case response)
interface LabProfileResponse {
  id?: string
  name?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  state_code?: string
  pincode?: string
  gstin?: string
  pan?: string
  phone?: string
  email?: string
  bank_name?: string
  account_holder_name?: string
  account_number?: string
  ifsc_code?: string
  account_type?: string
  category_code?: string
  upi_id?: string
  upi_qr_url?: string
  logo_url?: string
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient()
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [emailTo, setEmailTo] = useState('')
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoices', id],
    queryFn: async () => {
      const response = await api.get(`/invoices/${id}`)
      // API returns { data: invoice }, so we need response.data.data
      return response.data?.data as Invoice
    },
  })

  const { data: labProfile } = useQuery({
    queryKey: ['settings', 'lab-profile'],
    queryFn: async () => {
      const { data } = await api.get<{ data: LabProfileResponse | null }>('/settings/lab-profile')
      return data?.data || null
    },
  })

  // Generate UPI payment URL for QR code
  const generateUPIUrl = (upiId: string, name: string, amount?: number) => {
    if (!upiId) return ''
    const encodedName = encodeURIComponent(name || 'Payment')
    let url = `upi://pay?pa=${upiId}&pn=${encodedName}&cu=INR`
    if (amount && amount > 0) {
      url += `&am=${amount}`
    }
    return url
  }

  // Show bank section if at least one bank detail field exists
  const hasBankDetails = !!(
    labProfile?.bank_name ||
    labProfile?.account_holder_name ||
    labProfile?.account_number ||
    labProfile?.ifsc_code ||
    labProfile?.account_type ||
    labProfile?.category_code
  )
  const hasUpiDetails = !!(labProfile?.upi_id || labProfile?.upi_qr_url)
  const hasPaymentDetails = hasBankDetails || hasUpiDetails

  // Format lab address
  const getLabAddress = () => {
    if (!labProfile) return ''
    const parts = [
      labProfile.address_line1,
      labProfile.address_line2,
      labProfile.city,
      `${labProfile.state} - ${labProfile.pincode}`
    ].filter(Boolean)
    return parts.join(', ')
  }

  // Unified PDF handling - always use backend-generated PDF for consistency
  // This ensures Preview, Download, and Email attachment are identical
  const fetchInvoicePDF = async (): Promise<Blob | null> => {
    if (!id) return null
    try {
      const response = await api.get(`/invoices/${id}/pdf`, {
        responseType: 'blob',
      })
      return new Blob([response.data], { type: 'application/pdf' })
    } catch {
      toast.error('Failed to generate PDF')
      return null
    }
  }

  const handleDownloadPDF = async () => {
    const blob = await fetchInvoicePDF()
    if (!blob) return

    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Invoice_${invoice?.displayId || 'document'}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
    toast.success('Invoice downloaded')
  }

  const handlePrint = async () => {
    // Open backend-generated PDF in new tab for printing
    // This ensures print output matches downloaded/emailed PDF exactly
    const blob = await fetchInvoicePDF()
    if (!blob) return

    const url = window.URL.createObjectURL(blob)
    window.open(url, '_blank')
    // Revoke after delay to allow browser to load the PDF
    setTimeout(() => window.URL.revokeObjectURL(url), 10000)
  }

  const handlePreviewPDF = async () => {
    // Preview opens the same backend-generated PDF
    const blob = await fetchInvoicePDF()
    if (!blob) return

    const url = window.URL.createObjectURL(blob)
    window.open(url, '_blank')
    setTimeout(() => window.URL.revokeObjectURL(url), 10000)
  }

  const openEmailModal = () => {
    if (invoice?.doctor) {
      setEmailTo(invoice.doctor.email || '')
      setEmailSubject(`Invoice ${invoice.displayId} from ${labProfile?.name || 'Dental Lab'}`)
      setEmailMessage(`Dear ${invoice.doctor.name},\n\nPlease find attached invoice ${invoice.displayId} for ${formatCurrency(invoice.totalAmount)}.\n\nDue Date: ${formatDate(invoice.dueDate)}\n\nThank you for your business.\n\nBest regards,\n${labProfile?.name || 'Dental Lab'}`)
    }
    setShowEmailModal(true)
  }

  const sendEmailMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/invoices/${id}/send-email`, {
        to: emailTo,
        subject: emailSubject,
        message: emailMessage,
      })
    },
    onSuccess: () => {
      setShowEmailModal(false)
      toast.success('Invoice sent successfully')
    },
    onError: () => {
      toast.error('Failed to send invoice')
    },
  })

  const cancelInvoiceMutation = useMutation({
    mutationFn: async () => {
      return api.post(`/invoices/${id}/cancel`, {
        reason: cancelReason,
      })
    },
    onSuccess: () => {
      setShowCancelModal(false)
      setCancelReason('')
      queryClient.invalidateQueries({ queryKey: ['invoices', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast.success('Invoice cancelled successfully')
    },
    onError: (error: { response?: { data?: { detail?: string; code?: string; totalPaid?: number } } }) => {
      const data = error.response?.data
      if (data?.code === 'PAYMENTS_EXIST') {
        toast.error(data.detail || 'Cannot cancel invoice with payments')
      } else {
        toast.error(data?.detail || 'Failed to cancel invoice')
      }
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!invoice) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-file-invoice text-4xl text-gray-400 mb-4"></i>
        <p className="text-gray-500 dark:text-gray-400">Invoice not found</p>
        <Link to="/invoices" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to Invoices
        </Link>
      </div>
    )
  }

  const getStatusBadge = (status: InvoiceStatus) => {
    const variants: Record<InvoiceStatus, 'success' | 'warning' | 'error' | 'info' | 'gray'> = {
      draft: 'gray',
      sent: 'info',
      partial: 'warning',
      paid: 'success',
      overdue: 'error',
      cancelled: 'gray',
    }
    return <Badge variant={variants[status]}>{status}</Badge>
  }

  const isIntraState = invoice.address?.stateCode === labProfile?.state_code

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/invoices"
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <i className="fas fa-arrow-left"></i>
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{invoice.displayId}</h1>
              {getStatusBadge(invoice.status)}
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Issued {formatDate(invoice.issueDate)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          <Button variant="secondary" onClick={handlePrint}>
            <i className="fas fa-print mr-2"></i>
            Print
          </Button>
          <Button variant="secondary" onClick={handleDownloadPDF}>
            <i className="fas fa-download mr-2"></i>
            Download PDF
          </Button>
          {invoice.status !== 'cancelled' && (
            <Button variant="secondary" onClick={openEmailModal}>
              <i className="fas fa-envelope mr-2"></i>
              Send Email
            </Button>
          )}
          {invoice.status !== 'cancelled' && invoice.balanceAmount > 0 && (
            <Link to={`/payments?new=1&invoiceId=${invoice.id}`}>
              <Button>
                <i className="fas fa-plus mr-2"></i>
                Record Payment
              </Button>
            </Link>
          )}
          {invoice.status !== 'cancelled' && invoice.paidAmount === 0 && (
            <Button
              variant="secondary"
              onClick={() => setShowCancelModal(true)}
              className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <i className="fas fa-times-circle mr-2"></i>
              Cancel Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Cancelled Invoice Banner */}
      {invoice.status === 'cancelled' && (
        <div className="max-w-4xl mx-auto mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg no-print">
          <div className="flex items-start gap-3">
            <i className="fas fa-ban text-red-500 text-xl mt-0.5"></i>
            <div>
              <h3 className="font-semibold text-red-800 dark:text-red-200">Invoice Cancelled</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                This invoice was cancelled{invoice.cancelledAt && ` on ${formatDate(invoice.cancelledAt)}`}.
              </p>
              {invoice.cancelReason && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                  <strong>Reason:</strong> {invoice.cancelReason}
                </p>
              )}
              <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                Cancelled invoices are not included in outstanding balance calculations.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Document - Screen View */}
      <div className="no-print">
        <Card className="max-w-4xl mx-auto">
          {/* Invoice Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 pb-6 mb-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-primary-600">{labProfile?.name ?? 'DentaLabs'}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{getLabAddress()}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Phone: {labProfile?.phone} | Email: {labProfile?.email}
                </p>
                {labProfile?.gstin && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    <strong>GSTIN:</strong> {labProfile.gstin}
                  </p>
                )}
              </div>
              <div className="text-right">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">TAX INVOICE</h3>
                <p className="text-lg font-semibold text-primary-600 mt-2">{invoice.displayId}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  <strong>Date:</strong> {formatDate(invoice.issueDate)}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <strong>Due:</strong> {formatDate(invoice.dueDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Bill To</h4>
              <p className="font-semibold text-gray-900 dark:text-white">{invoice.doctor?.name}</p>
              <p className="text-gray-600 dark:text-gray-400">{invoice.doctor?.clinicName}</p>
              {invoice.address && (
                <>
                  <p className="text-gray-600 dark:text-gray-400">{invoice.address.line1}</p>
                  {invoice.address.line2 && <p className="text-gray-600 dark:text-gray-400">{invoice.address.line2}</p>}
                  <p className="text-gray-600 dark:text-gray-400">
                    {invoice.address.city}, {invoice.address.state} - {invoice.address.pincode}
                  </p>
                </>
              )}
              {invoice.doctor?.gstNumber && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  <strong>GSTIN:</strong> {invoice.doctor.gstNumber}
                </p>
              )}
            </div>
            <div className="text-right">
              <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Place of Supply</h4>
              <p className="text-gray-900 dark:text-white">{invoice.address?.state}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">State Code: {invoice.address?.stateCode}</p>
              {/* Work Order Reference */}
              {(invoice.workOrderNumber || invoice.workOrderReceivedDate) && (
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 text-left">
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Work Order</h4>
                  {invoice.workOrderNumber && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>WO #:</strong> {invoice.workOrderNumber}
                    </p>
                  )}
                  {invoice.workOrderReceivedDate && (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Received Date:</strong> {formatDate(invoice.workOrderReceivedDate)}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700">
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Description</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">HSN</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300">Qty</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300">Rate</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300">GST %</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {invoice.items?.map((item, index) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{index + 1}</td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white">{item.description}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 font-mono">{item.hsnCode}</td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{item.quantity}</td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{formatCurrency(item.unitPrice)}</td>
                    <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400">{item.gstPercent}%</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                      {formatCurrency(item.totalAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-80 space-y-2">
              <div className="flex justify-between py-2">
                <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(invoice.subtotal)}</span>
              </div>
              {/* GST is 0% for healthcare/dental services */}
              {(invoice.cgstAmount > 0 || invoice.sgstAmount > 0 || invoice.igstAmount > 0) && (
                isIntraState ? (
                  <>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600 dark:text-gray-400">CGST (0%):</span>
                      <span className="text-gray-900 dark:text-white">{formatCurrency(invoice.cgstAmount)}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600 dark:text-gray-400">SGST (0%):</span>
                      <span className="text-gray-900 dark:text-white">{formatCurrency(invoice.sgstAmount)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600 dark:text-gray-400">IGST (0%):</span>
                    <span className="text-gray-900 dark:text-white">{formatCurrency(invoice.igstAmount)}</span>
                  </div>
                )
              )}
              <div className="flex justify-between py-3 border-t-2 border-gray-300 dark:border-gray-600">
                <span className="font-bold text-gray-900 dark:text-white">Total:</span>
                <span className="font-bold text-xl text-gray-900 dark:text-white">{formatCurrency(invoice.totalAmount)}</span>
              </div>
              <div className="flex justify-between py-2 text-green-600">
                <span>Paid:</span>
                <span>{formatCurrency(invoice.paidAmount)}</span>
              </div>
              <div className="flex justify-between py-3 border-t border-gray-200 dark:border-gray-700">
                <span className="font-bold text-red-600">Balance Due:</span>
                <span className="font-bold text-xl text-red-600">{formatCurrency(invoice.balanceAmount)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Notes:</h4>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{invoice.notes}</p>
            </div>
          )}

          {/* Bank Details & UPI Section */}
          {hasPaymentDetails && (
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">
                <i className="fas fa-university mr-2"></i>
                Payment Details
              </h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bank Details */}
                {hasBankDetails && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <h5 className="font-medium text-gray-900 dark:text-white mb-3">Bank Transfer</h5>
                    <div className="space-y-2 text-sm">
                      {labProfile?.bank_name && (
                        <p className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Bank Name:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{labProfile.bank_name}</span>
                        </p>
                      )}
                      {(labProfile?.account_holder_name || labProfile?.name) && (
                        <p className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Account Holder:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{labProfile.account_holder_name || labProfile.name}</span>
                        </p>
                      )}
                      {labProfile?.account_number && (
                        <p className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Account Number:</span>
                          <span className="font-medium text-gray-900 dark:text-white font-mono">{labProfile.account_number}</span>
                        </p>
                      )}
                      {labProfile?.ifsc_code && (
                        <p className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">IFSC Code:</span>
                          <span className="font-medium text-gray-900 dark:text-white font-mono">{labProfile.ifsc_code}</span>
                        </p>
                      )}
                      {labProfile?.account_type && (
                        <p className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Account Type:</span>
                          <span className="font-medium text-gray-900 dark:text-white">{labProfile.account_type}</span>
                        </p>
                      )}
                      {labProfile?.category_code && (
                        <p className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Category Code:</span>
                          <span className="font-medium text-gray-900 dark:text-white font-mono">{labProfile.category_code}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* UPI Payment */}
                {hasUpiDetails && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <h5 className="font-medium text-gray-900 dark:text-white mb-3">UPI Payment</h5>
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 bg-white p-2 rounded-lg">
                        {labProfile?.upi_qr_url ? (
                          <img
                            src={labProfile.upi_qr_url}
                            alt="UPI QR Code"
                            className="w-24 h-24 object-contain"
                          />
                        ) : labProfile?.upi_id ? (
                          <QRCodeSVG
                            value={generateUPIUrl(labProfile.upi_id, labProfile.name || 'Payment', invoice.balanceAmount)}
                            size={96}
                            level="M"
                            includeMargin={false}
                          />
                        ) : null}
                      </div>
                      <div className="flex-1 space-y-2 text-sm">
                        {labProfile?.upi_id && (
                          <p className="flex flex-col">
                            <span className="text-gray-600 dark:text-gray-400">UPI ID:</span>
                            <span className="font-medium text-gray-900 dark:text-white font-mono">{labProfile.upi_id}</span>
                          </p>
                        )}
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Scan QR code or use UPI ID to pay via any UPI app (GPay, PhonePe, Paytm, etc.)
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-center text-sm text-gray-500 dark:text-gray-400">
            <p>Thank you for your business!</p>
            {labProfile?.email && (
              <p className="mt-1">For queries: reach out to <a href={`mailto:${labProfile.email}`} className="text-primary-600 hover:underline">{labProfile.email}</a></p>
            )}
          </div>
        </Card>
      </div>

      {/* Send Email Modal */}
      <Modal isOpen={showEmailModal} onClose={() => setShowEmailModal(false)} title="Send Invoice via Email" size="md">
        <div className="space-y-4">
          <Input
            label="To *"
            type="email"
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            placeholder="recipient@example.com"
          />
          <Input
            label="Subject *"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Invoice subject"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message
            </label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows={6}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Email message..."
            />
          </div>
          
          {/* PDF Attachment Preview */}
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500 text-white rounded">
                  <i className="fas fa-file-pdf"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Invoice_{invoice.displayId}.pdf
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    PDF attachment (will be auto-generated)
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handlePreviewPDF}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1"
              >
                <i className="fas fa-eye"></i>
                Preview
              </button>
            </div>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
              <i className="fas fa-info-circle mr-1"></i>
              This PDF will be automatically attached to the email
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowEmailModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => sendEmailMutation.mutate()}
              loading={sendEmailMutation.isPending}
              disabled={!emailTo || !emailSubject}
            >
              <i className="fas fa-paper-plane mr-2"></i>
              Send with PDF
            </Button>
          </div>
        </div>
      </Modal>

      {/* Cancel Invoice Modal */}
      <Modal isOpen={showCancelModal} onClose={() => setShowCancelModal(false)} title="Cancel Invoice" size="md">
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex gap-3">
              <i className="fas fa-exclamation-triangle text-amber-500 text-xl"></i>
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">Are you sure you want to cancel this invoice?</p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  This action cannot be undone. Cancelled invoices will not be included in outstanding balance calculations.
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Invoice Number:</span>
              <span className="font-medium text-gray-900 dark:text-white">{invoice.displayId}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-600 dark:text-gray-400">Amount:</span>
              <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(invoice.totalAmount)}</span>
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-gray-600 dark:text-gray-400">Status:</span>
              {getStatusBadge(invoice.status)}
            </div>
          </div>

          {invoice.paidAmount > 0 && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">
                <i className="fas fa-info-circle mr-2"></i>
                This invoice has received payments totaling {formatCurrency(invoice.paidAmount)}. 
                Invoices with payments cannot be cancelled.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reason for Cancellation (optional)
            </label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              placeholder="Enter reason for cancellation..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => { setShowCancelModal(false); setCancelReason(''); }}>
              Keep Invoice
            </Button>
            <Button
              variant="primary"
              onClick={() => cancelInvoiceMutation.mutate()}
              loading={cancelInvoiceMutation.isPending}
              disabled={invoice.paidAmount > 0}
              className="bg-red-600 hover:bg-red-700"
            >
              <i className="fas fa-times-circle mr-2"></i>
              Cancel Invoice
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
